/**
 * Amazon Pay API
 *
 * Endpoints:
 *   POST api/v1/amazon/sign/payload    - signPayload
 *   POST api/v1/amazon/signin/sign     - signSignInPayload
 *   POST api/v1/amazon/buyer           - redeemBuyerToken
 *   GET  api/v1/amazon/session/:id     - getCheckoutSession
 *   POST api/v1/amazon/session/:id     - updateCheckoutSession
 */
import { apiClientPostRequest, apiClientGetRequest } from '../lib/backendRequest';

// Region-specific Amazon Pay v2 checkout script. NA covers US + Canada and is
// the only region either web app currently ships to (see migration doc OQ-1).
// Add EU/JP variants here if the answer to OQ-1 changes.
export const AMAZON_PAY_V2_SCRIPT_URL = 'https://static-na.payments-amazon.com/checkout.js';

// Must stay in sync with `AMAZON_SIGNATURE_ALGORITHM` in photolane-backend's
// `lib/other/amazon_pay.rb`. Once the backend exposes the algorithm in the
// sign-payload response, callers should prefer that value over this constant.
export const AMAZON_PAY_V2_SIGNATURE_ALGORITHM = 'AMZN-PAY-RSASSA-PSS-V2';

const amazonApi = accessToken => ({
  /**
   * Sign payload. `payloadJSON` must be the exact JSON string the caller will
   * pass to Amazon as `payloadJSON` — server signs it byte-for-byte.
   *
   * @param {string} payloadJSON
   */
  async signPayload(payloadJSON) {
    try {
      const response = await apiClientPostRequest(
        `api/v1/amazon/sign/payload`,
        {
          data: payloadJSON,
        },
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * Sign the v2 SignIn payload. Unauthenticated — used before the buyer is
   * signed in. `payloadJSON` must be the exact JSON string handed to Amazon.
   *
   * @param {string} payloadJSON
   */
  async signSignInPayload(payloadJSON) {
    try {
      const response = await apiClientPostRequest(`api/v1/amazon/signin/sign`, {
        data: payloadJSON,
      });

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * Redeem the one-time buyerToken returned by the v2 SignIn redirect. Server
   * fetches the buyer identity from Amazon Pay. If `accessToken` was supplied
   * to amazonApi(), the server attaches Amazon as a provider to that account;
   * otherwise it signs the user in (or signs them up — needs `countryCode`).
   *
   * @param {string} buyerToken
   * @param {string} [countryCode] required for the sign-up path
   */
  async redeemBuyerToken(buyerToken, countryCode) {
    try {
      const response = await apiClientPostRequest(
        `api/v1/amazon/buyer`,
        { buyerToken, country_alpha2: countryCode },
        accessToken ? { Authorization: `Token ${accessToken}` } : {},
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.body || e.code };
    }
  },

  /**
   * GET checkout session details
   *
   * @param {string} amazonCheckoutSessionId
   */
  async getCheckoutSession(amazonCheckoutSessionId) {
    try {
      const response = await apiClientGetRequest(
        `api/v1/amazon/session/${amazonCheckoutSessionId}`,
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * UPDATE checkout session
   *
   * @param {string} amazonCheckoutSessionId
   */
  async updateCheckoutSession(amazonCheckoutSessionId, amount, currency) {
    try {
      const response = await apiClientPostRequest(
        `api/v1/amazon/session/${amazonCheckoutSessionId}`,
        {
          amount,
          currency_code: currency,
        },
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.body };
    }
  },
});

export default amazonApi;
